/*  FinalExamBuilder — Curated exam question editor for course authors.
    ────────────────────────────────────────────────────────────────────
    Mounted inside CourseSetup's "Assessment Settings" section when the
    author flips `has_final_exam` ON. Lets the author decide whether the
    exam is:
        • Curated     — the author writes/picks each question explicitly.
        • Auto-sample — the server picks random questions from the module
                        quiz bank at exam-start time (legacy behaviour).

    Backend endpoints:
        GET /api/course-builder/courses/{id}/final-exam
        PUT /api/course-builder/courses/{id}/final-exam

    Questions share the same shape as lesson-level QuizQuestion so the
    UX is consistent with QuizBuilder.jsx.
*/
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Question as QuestionIcon, Plus, Trash, CheckCircle, Sparkle, Info,
    Warning, ArrowClockwise, FloppyDisk, ListChecks, ToggleLeft,
    NotePencil,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { confirmDialog } from "@/lib/confirm";
import RichTextEditor from "@/components/creator/RichTextEditor";
import { htmlIsEmpty } from "@/components/creator/RichTextView";


const Q_TYPES = [
    { value: "mcq",        label: "MCQ",        Icon: ListChecks },
    { value: "true_false", label: "True/False", Icon: ToggleLeft },
    { value: "scenario",   label: "Scenario",   Icon: Sparkle },
];

const DEFAULT_TF = ["True", "False"];


function makeBlank(type = "mcq") {
    if (type === "true_false") {
        return { type, prompt: "", options: [...DEFAULT_TF], answer_index: 0, rationale: "" };
    }
    return {
        type,
        prompt: "",
        options: ["", "", "", ""],
        answer_index: 0,
        rationale: "",
    };
}


export default function FinalExamBuilder({ courseId, disabled, expectedCount, passScore }) {
    const [mode, setMode] = useState("curated");
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    const load = useCallback(async () => {
        if (!courseId) { setLoading(false); return; }
        setLoading(true);
        try {
            const { data } = await api.get(`/course-builder/courses/${courseId}/final-exam`);
            setMode(data.mode || "curated");
            setQuestions((data.questions || []).map((q) => ({
                id: q.id,
                type: q.type || "mcq",
                prompt: q.prompt || "",
                options: q.options || ["", "", "", ""],
                answer_index: q.answer_index ?? 0,
                rationale: q.rationale || "",
            })));
            setDirty(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load final exam.");
        } finally {
            setLoading(false);
        }
    }, [courseId]);

    useEffect(() => { load(); }, [load]);

    const setQ = (idx, patch) => {
        setQuestions((prev) => prev.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
        setDirty(true);
    };

    const addQuestion = (type = "mcq") => {
        setQuestions((prev) => [...prev, makeBlank(type)]);
        setDirty(true);
    };

    const removeQuestion = async (idx) => {
        const ok = await confirmDialog({
            title: "Remove this question?",
            message: "This question will be removed from the final exam. This action cannot be undone.",
            confirmText: "Remove",
            cancelText: "Keep",
            tone: "danger",
            testid: "fe-remove-question",
        });
        if (!ok) return;
        setQuestions((prev) => prev.filter((_, i) => i !== idx));
        setDirty(true);
    };

    const validate = () => {
        if (mode !== "curated") return true;
        for (let i = 0; i < questions.length; i += 1) {
            const q = questions[i];
            if (htmlIsEmpty(q.prompt)) { toast.error(`Q${i + 1}: add a prompt.`); return false; }
            if ((q.options || []).some((opt) => !opt.trim())) { toast.error(`Q${i + 1}: all options must be filled.`); return false; }
            if (q.answer_index == null || q.answer_index < 0 || q.answer_index >= q.options.length) {
                toast.error(`Q${i + 1}: pick a correct answer.`);
                return false;
            }
        }
        return true;
    };

    const save = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const payload = { mode, questions: mode === "curated" ? questions : [] };
            await api.put(`/course-builder/courses/${courseId}/final-exam`, payload);
            toast.success(mode === "curated" ? `Saved ${questions.length} final-exam question${questions.length === 1 ? "" : "s"}.` : "Auto-sample mode saved.");
            setDirty(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save final exam.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="text-xs text-muted-foreground italic py-4" data-testid="fe-loading">Loading final exam…</div>;
    }

    const countMismatch = expectedCount && mode === "curated" && questions.length > 0 && questions.length < Number(expectedCount);

    return (
        <div className="mt-4 pt-4 border-t border-border" data-testid="final-exam-builder">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                        <QuestionIcon size={12} weight="duotone" className="text-rose-600" />
                        Final Exam Builder
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground max-w-md leading-snug">
                        Choose how the final exam is assembled — curate specific questions yourself, or let the server sample from your module quizzes.
                    </div>
                </div>
                {dirty && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="fe-dirty-indicator">
                        Unsaved changes
                    </span>
                )}
            </div>

            {/* Mode picker */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3" data-testid="fe-mode-picker">
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => { setMode("curated"); setDirty(true); }}
                    className={`text-left rounded-lg border p-3 transition-colors disabled:opacity-60 ${
                        mode === "curated"
                            ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                            : "border-border bg-card hover:border-primary/40"
                    }`}
                    data-testid="fe-mode-curated"
                >
                    <div className="flex items-center gap-2">
                        <div className={`w-3.5 h-3.5 rounded-full border-2 ${mode === "curated" ? "bg-primary border-primary" : "border-muted-foreground/40"}`} />
                        <span className="font-bold text-sm">Curated (Recommended)</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1 leading-snug">
                        You write / hand-pick every exam question. Best when the exam should test specific outcomes.
                    </p>
                </button>
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => { setMode("auto_sample"); setDirty(true); }}
                    className={`text-left rounded-lg border p-3 transition-colors disabled:opacity-60 ${
                        mode === "auto_sample"
                            ? "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200"
                            : "border-border bg-card hover:border-emerald-200"
                    }`}
                    data-testid="fe-mode-auto-sample"
                >
                    <div className="flex items-center gap-2">
                        <div className={`w-3.5 h-3.5 rounded-full border-2 ${mode === "auto_sample" ? "bg-emerald-500 border-emerald-500" : "border-muted-foreground/40"}`} />
                        <span className="font-bold text-sm">Auto-sample</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1 leading-snug">
                        Server randomly samples N questions from your module quiz banks each attempt.
                    </p>
                </button>
            </div>

            {mode === "curated" ? (
                <>
                    {countMismatch ? (
                        <div className="mb-2 flex items-start gap-2 text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 leading-snug" data-testid="fe-count-warn">
                            <Warning size={12} weight="fill" className="text-amber-700 shrink-0 mt-0.5" />
                            <div>
                                You&apos;ve authored <b>{questions.length}</b> question{questions.length === 1 ? "" : "s"} but told doctors the exam has <b>{expectedCount}</b>. Match the count or update the exam config above.
                            </div>
                        </div>
                    ) : null}

                    {questions.length === 0 ? (
                        <div className="text-center py-6 border border-dashed border-border rounded-xl bg-secondary/20" data-testid="fe-empty">
                            <QuestionIcon size={28} weight="duotone" className="text-muted-foreground mx-auto mb-2" />
                            <div className="text-sm font-semibold">No questions yet</div>
                            <p className="text-[11px] text-muted-foreground mt-1 max-w-md mx-auto">
                                Add your first final-exam question. Doctors need to score {Math.round((passScore || 0.7) * 100)}% to earn the certificate.
                            </p>
                            <div className="flex items-center justify-center gap-2 mt-3">
                                <Button size="sm" onClick={() => addQuestion("mcq")} disabled={disabled} data-testid="fe-add-first-mcq">
                                    <Plus size={12} weight="bold" className="mr-1" /> Add MCQ
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => addQuestion("true_false")} disabled={disabled} data-testid="fe-add-first-tf">
                                    Add True/False
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => addQuestion("scenario")} disabled={disabled} data-testid="fe-add-first-scenario">
                                    Add Scenario
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-3" data-testid="fe-question-list">
                            {questions.map((q, idx) => (
                                <QuestionEditor
                                    key={idx}
                                    idx={idx}
                                    q={q}
                                    disabled={disabled}
                                    onChange={(patch) => setQ(idx, patch)}
                                    onRemove={() => removeQuestion(idx)}
                                />
                            ))}
                        </div>
                    )}

                    {questions.length > 0 && (
                        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                            <div className="text-[11px] text-muted-foreground">
                                <b className="text-foreground">{questions.length}</b> question{questions.length === 1 ? "" : "s"} authored
                                {expectedCount ? ` · exam shows ${expectedCount}` : ""}
                                {passScore ? ` · pass ${Math.round(passScore * 100)}%` : ""}
                            </div>
                            <div className="flex items-center gap-2">
                                <Button size="sm" variant="outline" onClick={() => addQuestion("mcq")} disabled={disabled} data-testid="fe-add-mcq">
                                    <Plus size={11} weight="bold" className="mr-1" /> MCQ
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => addQuestion("true_false")} disabled={disabled} data-testid="fe-add-tf">
                                    <Plus size={11} weight="bold" className="mr-1" /> True/False
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => addQuestion("scenario")} disabled={disabled} data-testid="fe-add-scenario">
                                    <Plus size={11} weight="bold" className="mr-1" /> Scenario
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <div className="p-3 border border-emerald-200 bg-emerald-50/50 rounded-lg text-[12px] text-emerald-900 leading-snug" data-testid="fe-auto-sample-note">
                    <div className="flex items-start gap-2">
                        <Info size={12} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
                        <div>
                            <b>Auto-sample mode is on.</b> Each time a doctor starts the final exam, the server picks {expectedCount || 20} random questions from every module quiz in this course. You don&apos;t need to author anything here — but make sure your module quizzes have enough good questions to sample from.
                        </div>
                    </div>
                </div>
            )}

            {/* Save bar */}
            <div className="mt-4 flex items-center justify-between gap-2 pt-3 border-t border-border">
                <div className="text-[11px] text-muted-foreground">
                    {dirty ? "Save to persist changes." : "Up to date."}
                </div>
                <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={load} disabled={saving || !dirty} data-testid="fe-revert-btn">
                        <ArrowClockwise size={11} weight="bold" className="mr-1" /> Revert
                    </Button>
                    <Button size="sm" onClick={save} disabled={saving || disabled || !dirty} data-testid="fe-save-btn">
                        <FloppyDisk size={11} weight="fill" className="mr-1" />
                        {saving ? "Saving…" : "Save Final Exam"}
                    </Button>
                </div>
            </div>
        </div>
    );
}


function QuestionEditor({ idx, q, disabled, onChange, onRemove }) {
    const TypeIcon = Q_TYPES.find((t) => t.value === q.type)?.Icon || QuestionIcon;
    const isTF = q.type === "true_false";

    const setOption = (oIdx, value) => {
        const options = [...q.options];
        options[oIdx] = value;
        onChange({ options });
    };

    const addOption = () => {
        if (isTF || q.options.length >= 6) return;
        onChange({ options: [...q.options, ""] });
    };

    const removeOption = (oIdx) => {
        if (isTF || q.options.length <= 2) return;
        const options = q.options.filter((_, i) => i !== oIdx);
        // Bump answer_index down if it was past the removed slot.
        let ai = q.answer_index;
        if (ai === oIdx) ai = 0;
        else if (ai > oIdx) ai -= 1;
        onChange({ options, answer_index: ai });
    };

    return (
        <div className="bg-card border border-border rounded-xl p-3 space-y-3" data-testid={`fe-question-${idx}`}>
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-md bg-rose-50 text-rose-700 grid place-items-center text-[10px] font-bold shrink-0">
                        Q{idx + 1}
                    </div>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        <TypeIcon size={10} weight="fill" /> {Q_TYPES.find((t) => t.value === q.type)?.label}
                    </span>
                </div>
                <button
                    type="button"
                    onClick={onRemove}
                    disabled={disabled}
                    className="w-7 h-7 grid place-items-center rounded-md hover:bg-rose-50 text-muted-foreground hover:text-rose-600 disabled:opacity-40 transition-colors"
                    data-testid={`fe-question-${idx}-delete`}
                    title="Remove question"
                >
                    <Trash size={12} weight="duotone" />
                </button>
            </div>

            {/* Prompt */}
            <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Question prompt</label>
                <div className="mt-1">
                    <RichTextEditor
                        value={q.prompt || ""}
                        onChange={(html) => onChange({ prompt: html })}
                        placeholder={q.type === "scenario" ? "Describe the clinical scenario, then ask the question…" : "Type the question the doctor must answer…"}
                        disabled={disabled}
                        size="sm"
                    />
                </div>
                <input type="hidden" data-testid={`fe-question-${idx}-prompt`} value={q.prompt || ""} readOnly />
            </div>

            {/* Options */}
            <div>
                <div className="flex items-center justify-between gap-2 mb-1">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Options</label>
                    {!isTF && q.options.length < 6 && (
                        <button
                            type="button"
                            onClick={addOption}
                            disabled={disabled}
                            className="text-[11px] font-bold text-primary hover:underline disabled:opacity-40"
                            data-testid={`fe-question-${idx}-add-option`}
                        >
                            + Add option
                        </button>
                    )}
                </div>
                <div className="space-y-1.5">
                    {q.options.map((opt, oIdx) => {
                        const correct = q.answer_index === oIdx;
                        return (
                            <div key={oIdx} className={`flex items-center gap-2 p-1.5 rounded-md border ${correct ? "border-emerald-400 bg-emerald-50/50" : "border-border bg-secondary/20"}`}>
                                <button
                                    type="button"
                                    onClick={() => onChange({ answer_index: oIdx })}
                                    disabled={disabled}
                                    className={`w-5 h-5 rounded-full border-2 grid place-items-center shrink-0 transition-colors ${
                                        correct ? "bg-emerald-500 border-emerald-500 text-white" : "border-muted-foreground/40 hover:border-emerald-400"
                                    }`}
                                    title={correct ? "Correct answer" : "Mark as correct"}
                                    data-testid={`fe-question-${idx}-option-${oIdx}-correct`}
                                >
                                    {correct && <CheckCircle size={11} weight="fill" />}
                                </button>
                                <Input
                                    value={opt}
                                    onChange={(e) => setOption(oIdx, e.target.value)}
                                    disabled={disabled || isTF}
                                    placeholder={isTF ? opt : `Option ${oIdx + 1}`}
                                    className="h-8 text-sm border-0 bg-transparent focus-visible:ring-0 flex-1"
                                    data-testid={`fe-question-${idx}-option-${oIdx}`}
                                />
                                {!isTF && q.options.length > 2 && (
                                    <button
                                        type="button"
                                        onClick={() => removeOption(oIdx)}
                                        disabled={disabled}
                                        className="w-6 h-6 grid place-items-center rounded-md text-muted-foreground hover:text-rose-600 hover:bg-rose-50 disabled:opacity-30 transition-colors"
                                        title="Remove option"
                                        data-testid={`fe-question-${idx}-option-${oIdx}-remove`}
                                    >
                                        <Trash size={10} />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
                <div className="mt-1 text-[10px] text-muted-foreground flex items-center gap-1">
                    <CheckCircle size={10} weight="fill" className="text-emerald-500" /> Click the green circle to mark the correct answer.
                </div>
            </div>

            {/* Rationale */}
            <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                    <NotePencil size={10} weight="duotone" /> Rationale (shown after submission)
                </label>
                <div className="mt-1">
                    <RichTextEditor
                        value={q.rationale || ""}
                        onChange={(html) => onChange({ rationale: html })}
                        placeholder="Explain why the correct answer is correct — doctors see this after submitting the exam."
                        disabled={disabled}
                        size="sm"
                    />
                </div>
                <input type="hidden" data-testid={`fe-question-${idx}-rationale`} value={q.rationale || ""} readOnly />
            </div>
        </div>
    );
}
